/**
 * Taking the money.
 *
 * One row per tender the till accepts, all three visible at once rather than a method
 * picker: a split across cash and card is ordinary at a counter, and a customer who says
 * "two hundred on the card and the rest cash" should not make the cashier hunt for a
 * "split" button. Amounts are typed into the row; what each method *settles* is worked out
 * on the way out, and cash over the balance becomes change.
 *
 * Keyboard first, because the customer is waiting: the cash box has focus on open, Enter
 * takes the payment, and the denomination buttons are there for the mouse that may not be.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import {
  TENDER_LABELS,
  TENDER_METHODS,
  formatMoney,
  type CheckoutTender,
  type TenderMethod,
} from '@/features/pos/api'
import {
  changeCents,
  fromCents,
  outstandingCents,
  settlementTenders,
  tenderProblem,
  toCents,
  type TenderDraft,
} from '@/features/pos/cart'

/** Notes a cashier reaches for. Rounded up from the balance, so they are always useful. */
function suggestions(balanceCents: number): number[] {
  if (balanceCents <= 0) return []
  const notes = [5_000, 10_000, 20_000, 50_000, 100_000, 200_000]
  const above = notes.filter((note) => note > balanceCents).slice(0, 3)
  const roundedUp = Math.ceil(balanceCents / 10_000) * 10_000
  return [...new Set([balanceCents, roundedUp, ...above])].slice(0, 4)
}

const emptyDrafts = (): TenderDraft[] =>
  TENDER_METHODS.map((method) => ({ method, amount: '', reference: '' }))

export interface PaymentResult {
  payments: CheckoutTender[]
  /** What was physically handed over, which is not what the cash tender settles. */
  cash_tendered: string
}

export function PaymentDialog({
  open,
  onClose,
  totalCents,
  storeCreditCents,
  pending,
  error,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  totalCents: number
  /** The attached customer's wallet, or null when the sale is a walk-in. */
  storeCreditCents: number | null
  pending: boolean
  error: string | null
  onConfirm: (result: PaymentResult) => void
}) {
  const [drafts, setDrafts] = useState<TenderDraft[]>(emptyDrafts)
  const [touched, setTouched] = useState(false)
  const cashBox = useRef<HTMLInputElement>(null)

  // A fresh dialog every time it opens. Carrying the last sale's split into the next
  // customer is the kind of state bug that charges somebody else's card.
  useEffect(() => {
    if (!open) return
    setDrafts(emptyDrafts())
    setTouched(false)

    // After the modal has placed focus, not before: `Modal` moves focus to its first
    // control on open, and a cashier who has to Tab past a close button to key in cash is
    // a cashier reaching for the mouse. The microtask runs once React has flushed the
    // effects for this commit, which is where the modal does its focusing.
    queueMicrotask(() => cashBox.current?.focus())
  }, [open])

  const set = (method: TenderMethod, patch: Partial<TenderDraft>) =>
    setDrafts((rows) => rows.map((row) => (row.method === method ? { ...row, ...patch } : row)))

  const outstanding = outstandingCents(totalCents, drafts)
  const change = changeCents(totalCents, drafts)
  const problem = useMemo(
    () => tenderProblem(totalCents, drafts, storeCreditCents),
    [totalCents, drafts, storeCreditCents],
  )

  // The problem is worth saying as soon as there is something to say it about: the button
  // is dead while a tender is short, and a dead button with no reason beside it is a
  // cashier tapping it harder while the customer waits.
  const started = drafts.some((row) => toCents(row.amount) > 0)

  const confirm = () => {
    setTouched(true)
    if (problem || pending) return
    const cash = drafts.find((row) => row.method === 'cash')
    onConfirm({
      payments: settlementTenders(totalCents, drafts),
      cash_tendered: fromCents(toCents(cash?.amount ?? '')),
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Payment"
      description={`${formatMoney(fromCents(totalCents))} due`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Back to cart
          </Button>
          <Button onClick={confirm} loading={pending} disabled={Boolean(problem)}>
            Take payment
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          confirm()
        }}
      >
        {/* A submit input the eye never finds: Enter in any amount box takes the payment,
            which is how a till is worked, without the browser picking a button for us. */}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />

        <div className="rounded-md bg-slate-50 p-4 text-center dark:bg-slate-800/60">
          <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Amount due
          </p>
          <p className="text-3xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {formatMoney(fromCents(totalCents))}
          </p>
        </div>

        {drafts.map((row) => {
          const unavailable = row.method === 'store_credit' && storeCreditCents == null
          return (
            <div key={row.method} className="space-y-1.5">
              <div className="flex items-center gap-3">
                <label
                  htmlFor={`tender-${row.method}`}
                  className="w-32 shrink-0 text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {TENDER_LABELS[row.method]}
                </label>
                <Input
                  id={`tender-${row.method}`}
                  ref={row.method === 'cash' ? cashBox : undefined}
                  inputMode="decimal"
                  placeholder="0.00"
                  className="text-right tabular-nums"
                  disabled={unavailable || pending}
                  value={row.amount}
                  onChange={(event) => set(row.method, { amount: event.target.value })}
                />
              </div>

              {row.method === 'card' && toCents(row.amount) > 0 && (
                <div className="flex items-center gap-3">
                  <span className="w-32 shrink-0" />
                  <Input
                    aria-label="Card reference"
                    placeholder="Approval or last 4 digits (optional)"
                    disabled={pending}
                    value={row.reference}
                    onChange={(event) => set(row.method, { reference: event.target.value })}
                  />
                </div>
              )}

              {row.method === 'store_credit' && (
                <p className="pl-[8.75rem] text-xs text-slate-500 dark:text-slate-400">
                  {storeCreditCents == null
                    ? 'Attach a customer to spend store credit.'
                    : `${formatMoney(fromCents(storeCreditCents))} on the account.`}
                </p>
              )}
            </div>
          )
        })}

        <div className="flex flex-wrap gap-2">
          {suggestions(Math.max(0, outstanding)).map((amount) => (
            <Button
              key={amount}
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={() =>
                set('cash', {
                  amount: fromCents(
                    toCents(drafts.find((row) => row.method === 'cash')?.amount ?? '') + amount,
                  ),
                })
              }
            >
              {formatMoney(fromCents(amount))}
            </Button>
          ))}
          {drafts.some((row) => row.amount) && (
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => setDrafts(emptyDrafts())}
            >
              Clear
            </Button>
          )}
        </div>

        <dl className="space-y-1 border-t border-slate-200 pt-3 text-sm dark:border-slate-800">
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Still to pay</dt>
            <dd className="tabular-nums font-medium">
              {formatMoney(fromCents(Math.max(0, outstanding)))}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">Change</dt>
            <dd className="tabular-nums font-medium text-brand-700 dark:text-brand-300">
              {formatMoney(fromCents(change))}
            </dd>
          </div>
        </dl>

        {error && <Alert tone="error">{error}</Alert>}
        {!error && (touched || started) && problem && <Alert tone="error">{problem}</Alert>}
      </form>
    </Modal>
  )
}
